import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View, Text, useWindowDimensions } from "react-native";
import { useReactiveVar } from "react-reactive-var";
import AnnouncementBanner from "../Components/AnnouncementBanner";
import AppInstallBanner from "../Components/AppInstallBanner";
import ReleaseNotesModal from "../Components/ReleaseNotesModal";
import NewsletterConsentModal from "../Components/NewsletterConsentModal";
import ToothFairyLoadingPlaceholder from "../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useReleaseNotes from "../Hooks/useReleaseNotes";
import useNewsletterConsent from "../Hooks/useNewsletterConsent";
import useElectronWorkspace from "../Hooks/useElectronWorkspace";
import useWorkspaceLogo from "../Hooks/useWorkspaceLogo";
import usersAPI from "../API/Users/usersAPI";
import {
  AppRootContext,
  isBillingAddressSet,
  isElectronRunning,
  isFirstTrialStarted,
  isUserDelinquent,
  isUserOnTrial,
  isUserWithNoPaymentMethod,
  userType,
  wasWorkspaceJustCreated,
} from "../../App";
import {
  forcedElectronChrome,
  forcedElectronChromePlatform,
  isElectronChrome,
} from "../Utils/forceElectronChrome";
import SideMenu from "../Components/SideMenu";
import TopBar from "../Components/TopBar";
import TfcodeSidePanel from "../Components/TfcodeSidePanel";
import Constants from "../Constants";
import usePath from "../Hooks/usePath";
import useWorkspaces from "../Hooks/useWorkspaces";
import { readFavouriteApp } from "../Utils/favouriteApp";
import { useHistory } from "../Router";
import { routes } from "../Router/routes";
import modelsHelper from "../API/Models/modelsHelper";
import { globalAppConfig, isLargeScreen } from "./MainViewContainer";
import envConfig from "../../envConfig";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
//
// Map a tfcode "app_route" navigation signal to a host app route path.
// `target` is one of the host `routes` keys (settings, admin, dashboard, ...)
// and `params` supplies the dynamic segments (sectionId, agentId, workspaceId, ...).
const routeFromSignal = (target, params, workspaceId) => {
  const template = typeof routes?.[target] === "string" ? routes[target] : null;
  if (!template) return null;
  const segs = template.split("/");
  const filled = segs.map((seg) => {
    if (!seg.startsWith(":")) return seg;
    // Strip the optional-segment marker (e.g. `:sectionId?` -> `sectionId`)
    // so the param key matches what callers pass in `params`. Without this,
    // every optional segment resolves to "" and sections like admin/settings/
    // profile navigate without their sectionId and render a blank screen.
    const key = seg.slice(1).replace(/\?$/, "");
    if (key === "workspaceId") return workspaceId;
    return params?.[key] || "";
  });
  const joined = filled.filter(Boolean).join("/");
  return "/" + joined;
};

// Derive the tfcode backend origin from the embed panel URL (falls back to the
// local dev backend on :4096, or env-driven origin in production).
const backendOriginFrom = (panelUrl) => {
  try {
    if (panelUrl) return new URL(panelUrl).origin;
  } catch {}
  const envOrigin =
    typeof envConfig !== "undefined" && envConfig?.REACT_APP_TFCODE_ORIGIN;
  return envOrigin || "http://localhost:4096";
};

// Capture the current host app view (html2canvas). We capture the main content
// area instead of document.body to avoid grabbing modals/overlays. The tfcode
// overlay panel is temporarily hidden during capture.
const captureHostScreenshot = async (id, panelUrl) => {
  console.log("[tfcode-screenshot] capture requested", { id, panelUrl });
  let html2canvas;
  try {
    const mod = await import("html2canvas");
    html2canvas = mod.default || mod;
    console.log("[tfcode-screenshot] html2canvas loaded");
  } catch (err) {
    console.error(
      "[tfcode-screenshot] html2canvas import FAILED — run `yarn add html2canvas` in app/app",
      err,
    );
    return;
  }
  const panels = Array.from(document.querySelectorAll("[data-tfcode-panel]"));
  const prev = panels.map((el) => ({ el, display: el.style.display }));
  panels.forEach((el) => (el.style.display = "none"));
  // Use the main app content area; fall back to document.body if not found.
  const captureEl =
    document.querySelector("[data-app-content]") || document.body;
  try {
    const canvas = await html2canvas(captureEl, {
      useCORS: true,
      logging: false,
      backgroundColor: null,
    });
    console.log("[tfcode-screenshot] canvas rendered", {
      w: canvas.width,
      h: canvas.height,
    });
    const image = canvas.toDataURL("image/png");
    const origin = backendOriginFrom(panelUrl);
    console.log(
      "[tfcode-screenshot] posting back to",
      `${origin}/screenshot/${id}`,
      { bytes: image.length },
    );
    const res = await fetch(`${origin}/screenshot/${id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image }),
    });
    console.log("[tfcode-screenshot] backend responded", res.status);
  } finally {
    prev.forEach(({ el, display }) => (el.style.display = display));
  }
};
//
const MainViewScreen = ({ children, height, sideMenuOpen, width }) => {
  const history = useHistory();
  const { isInWorkspace } = usePath();
  const { dispatch, state } = AppStateManager.useAppStateManager();
  const { width: screenWidth } = useWindowDimensions();
  // Platform detection for Electron (real runtime or scoped browser override)
  const forcedChrome = useReactiveVar(forcedElectronChrome);
  const forcedPlatform = useReactiveVar(forcedElectronChromePlatform);
  const [realPlatform, setRealPlatform] = useState(null);
  useEffect(() => {
    if (isElectronRunning() && window.electronAPI && !forcedChrome) {
      window.electronAPI.getPlatform().then(setRealPlatform);
    }
  }, [forcedChrome]);
  const chrome = isElectronChrome();
  const platform = forcedChrome ? forcedPlatform : realPlatform;

  const _styles = styles({
    height,
    sideMenuOpen,
    width,
    pageNotFound: state?.pageNotFound,
    isLargeScreen: isLargeScreen(),
    electron: chrome,
    platform,
  });
  const { t, i18n } = useTranslation();
  const appConfig = useReactiveVar(globalAppConfig);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [announcementData, setAnnouncementData] = useState(null);
  // Active tab: boot into the user's favourite app when one is set
  // (right-click menu on the tab strip), otherwise App. When the favourite
  // is Code/Design but no workspace is active yet, the App surface still
  // shows (no iframe url until a workspace is picked) — the auto-switch
  // effect below keeps the favourite tab once a workspace is entered.
  const [activeTab, setActiveTab] = useState(() => readFavouriteApp() || "app");
  // Apps the user chose to install (unified desktop installer). Defaults to
  // all apps for web builds and older desktop builds without the bridge.
  const [installedApps, setInstalledApps] = useState(["app", "code", "design"]);
  useEffect(() => {
    if (!isElectronRunning() || !window.electronAPI?.getSelectedApps) return;
    window.electronAPI
      .getSelectedApps()
      .then((config) => {
        if (Array.isArray(config?.apps) && config.apps.length) {
          setInstalledApps(config.apps);
        }
      })
      .catch(() => {});
  }, []);
  // Live-refresh when the in-session wizard changes the selection so the
  // active tab falls back to App if the tab is deselected.
  useEffect(() => {
    if (!isElectronRunning() || !window.electronAPI?.onSelectedAppsChanged) return;
    return window.electronAPI.onSelectedAppsChanged((config) => {
      if (Array.isArray(config?.apps) && config.apps.length) {
        setInstalledApps(config.apps);
      }
    });
  }, []);
  const soleApp = installedApps.length === 1 ? installedApps[0] : null;
  // Persist the last app so workspace changes / reloads land back on it.
  useEffect(() => {
    try {
      localStorage.setItem("tf_last_app", activeTab);
    } catch (_) {}
  }, [activeTab]);
  // The App tab hosts sign-in, workspace list/selection, settings, and
  // general navigation. Code and Design require a workspace to be chosen first,
  // so the app ALWAYS boots into App regardless of which apps are installed.
  // Single-app installs hide the tab strip, but the workspace flow is still
  // inside the App surface.
  useEffect(() => {
    // If the current tab isn't available (e.g. last-used Code was
    // uninstalled), fall back to App.
    if (!installedApps.includes(activeTab)) {
      setActiveTab("app");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installedApps]);

  // When App is not in the user's selection, auto-switch to the last-used
  // non-App app after a workspace is selected (or to the first available one
  // on first use). useLayoutEffect runs before paint so the App content
  // never becomes visible — prevents the one-frame flicker of the workspace
  // dashboard before the Code/Design iframe mounts.
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const prevInWorkspaceRef = useRef(null);
  useLayoutEffect(() => {
    // On boot (wasInWorkspace unknown) keep the favourite tab.
    const wasInWorkspace = prevInWorkspaceRef.current;
    prevInWorkspaceRef.current = isInWorkspace;
    // Leaving a workspace (workspace grid, profile, general settings...) lands
    // on the App tab: the App surface hosts the workspace grid etc. When the
    // user only BOOTED there, the favourite tab must survive.
    if (!isInWorkspace && wasInWorkspace === true) {
      if (activeTabRef.current !== "app") setActiveTab("app");
      return;
    }
    if (activeTabRef.current !== "app" || installedApps.includes("app")) return;
    const last = (() => {
      try {
        return localStorage.getItem("tf_last_app");
      } catch (_) {
        return null;
      }
    })();
    const target =
      last && last !== "app" && installedApps.includes(last)
        ? last
        : installedApps.find((a) => a !== "app");
    if (target && target !== "app") {
      setActiveTab(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInWorkspace, installedApps]);
  // Design has no background pre-start (unlike Code, which the main process
  // starts once agents load): when Design is the active tab and no design
  // server is up yet, start it directly. Covers both a Design-only install
  // and booting into a favourited Design tab (the tab switch handler only
  // runs on user clicks, so booting there would otherwise sit on "Loading
  // Design" forever). A workspace must be active first; the effect re-runs
  // once one is selected.
  useEffect(() => {
    if (activeTab !== "design" || tfdesignUrl) return;
    // The sidecar needs the workspace token (main process rejects the start
    // without it), so wait until the active workspace's token has loaded —
    // this re-runs when the workspace list arrives.
    const token = state?.workspaces?.find(
      (w) => w?.id === state?.activeWorkspaceID,
    )?.workspaceToken;
    if (!token) return;
    if (!window.electronAPI?.startTfdesign) return;
    let cancelled = false;
    let attempts = 0;
    (async () => {
      // IPC ordering race: the renderer's set-active-workspace may not have
      // been processed by main yet ("Please select a workspace first") —
      // retry until it lands instead of staying stuck on Loading Design.
      while (!cancelled && attempts < 15) {
        attempts += 1;
        try {
          const { port } = await window.electronAPI.startTfdesign();
          if (cancelled) return;
          const designTheme = mode === "dark" ? "dark" : "light";
          setTfdesignUrl(
            `http://localhost:${port}?embed=electron&theme=${designTheme}`,
          );
          return;
        } catch (error) {
          console.error("[MainView] Design auto-start failed:", error);
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soleApp, activeTab, tfdesignUrl, state?.activeWorkspaceID, state?.workspaces]);
  const [tfcodeUrl, setTfcodeUrl] = useState(null);
  const [tfcodePanelUrl, setTfcodePanelUrl] = useState(null);
  const [codeReady, setCodeReady] = useState(false);
  // True while the Code tab reloads because the user switched workspaces —
  // the sidecar restarts and re-syncs, so the splash says "Syncing profile…".
  const [switchedWorkspace, setSwitchedWorkspace] = useState(false);
  // Bumping this remounts the Code iframe (blank-screen recovery).
  const [codeReloadKey, setCodeReloadKey] = useState(0);
  const prevWorkspaceIdRef = useRef(null);
  const [tfdesignUrl, setTfdesignUrl] = useState(null);
  const [designReady, setDesignReady] = useState(false);
  const [designError, setDesignError] = useState(null);
  const [designModels, setDesignModels] = useState([]);
  const iframeRef = useRef(null);
  const designIframeRef = useRef(null);
  const latestBoundFolders = useRef(null);
  const latestWorkspaceLogo = useRef(null);
  const embedPlatform = useRef(null);
  const tfcodePanelUrlRef = useRef(null);
  const panelIframeRef = useRef(null);
  const { currentTheme, mode } = AppTheme.useTheme();

  const workspaceLogo = useWorkspaceLogo({ isInWorkspace });

  useEffect(() => {
    latestWorkspaceLogo.current = workspaceLogo;
  }, [workspaceLogo]);

  useEffect(() => {
    tfcodePanelUrlRef.current = tfcodePanelUrl;
  }, [tfcodePanelUrl]);

  // Report the current host route to the embedded tfcode so host-aware tools
  // (app_current_route) know where the user is. Pushed on path change and when
  // the panel (re)mounts.
  useEffect(() => {
    const post = () => {
      const iframe = panelIframeRef.current;
      if (!iframe?.contentWindow) return;
      const targetOrigin = (() => {
        const src = iframe.src;
        if (!src) return "*";
        try {
          return new URL(src).origin;
        } catch {
          return "*";
        }
      })();
      iframe.contentWindow.postMessage(
        {
          v: 1,
          type: "tfcode-host-route",
          route: history.location?.pathname || "",
        },
        targetOrigin,
      );
    };
    post();
    const unlisten = history.listen(post);
    return unlisten;
  }, [history, tfcodePanelUrl]);

  useEffect(() => {
    embedPlatform.current =
      platform === "darwin"
        ? "macos"
        : platform === "win32"
          ? "windows"
          : platform === "linux"
            ? "linux"
            : null;
  }, [platform]);

  // Compute and store bound folders; send to tfcode when both data and iframe are ready
  useEffect(() => {
    const activeWorkspaceId = state?.activeWorkspaceID;
    const allChats = state?.chats || [];
    const chats = allChats.filter((c) => c?.workspaceID === activeWorkspaceId);
    const folders = chats
      .filter((c) => {
        const meta =
          typeof c?.metadata === "string"
            ? JSON.parse(c.metadata)
            : c?.metadata;
        return !!meta?.folderBinding?.path;
      })
      .map((c) => {
        const meta =
          typeof c?.metadata === "string"
            ? JSON.parse(c.metadata)
            : c?.metadata;
        return {
          path: meta.folderBinding.path,
          name:
            meta.folderBinding.name ||
            meta.folderBinding.path.split("/").pop() ||
            meta.folderBinding.path.split("\\").pop(),
          chatId: c.id,
          boundAt: meta.folderBinding.boundAt || "1970-01-01",
        };
      })
      .sort(
        (a, b) => new Date(b.boundAt).getTime() - new Date(a.boundAt).getTime(),
      );
    const seen = new Set();
    const unique = folders.filter((f) => {
      if (seen.has(f.path)) return false;
      seen.add(f.path);
      return true;
    });
    const recent = unique.slice(0, 10);
    // Always store latest so tfcode-ready can use it even if iframe wasn't mounted yet
    latestBoundFolders.current = recent;
    const iframe = iframeRef.current;
    if (iframe?.contentWindow && tfcodeUrl) {
      iframe.contentWindow.postMessage(
        { type: "tfcode-bound-folders", folders: recent },
        "*",
      );
    } else if (recent.length > 0) {
      window?.electronAPI?.writeLog?.("warn", "bound-folders postMessage skipped", {
        reason: iframe?.contentWindow ? "url-missing" : "iframe-missing",
        count: recent.length,
      });
    }
    const panelIframe = panelIframeRef.current;
    if (panelIframe?.contentWindow && tfcodePanelUrlRef.current) {
      panelIframe.contentWindow.postMessage(
        { v: 1, type: "tfcode-bound-folders", folders: recent },
        "*",
      );
    } else if (recent.length > 0) {
      window?.electronAPI?.writeLog?.("warn", "bound-folders panel postMessage skipped", {
        reason: panelIframe?.contentWindow ? "url-missing" : "iframe-missing",
        count: recent.length,
      });
    }
  }, [state?.chats, state?.activeWorkspaceID, tfcodeUrl]);

  // Reset codeReady when tfcodeUrl changes (new workspace, new server)
  useEffect(() => {
    setCodeReady(false);
  }, [tfcodeUrl]);

  // Track workspace switches (a previous workspace existed and id changed) so
  // the Code splash can say "Syncing profile…" instead of "Loading Code" —
  // the sidecar restarts and re-syncs tools for the new workspace.
  useEffect(() => {
    const prev = prevWorkspaceIdRef.current;
    prevWorkspaceIdRef.current = state?.activeWorkspaceID ?? null;
    if (prev && state?.activeWorkspaceID && prev !== state.activeWorkspaceID) {
      setSwitchedWorkspace(true);
      if (window?.electronAPI?.writeLog) {
        window.electronAPI.writeLog("info", "Workspace switch detected", {
          from: prev,
          to: state.activeWorkspaceID,
        });
      }
    }
  }, [state?.activeWorkspaceID]);

  useEffect(() => {
    if (codeReady) setSwitchedWorkspace(false);
  }, [codeReady]);

  // Reset designReady when tfdesignUrl changes (new workspace, new server)
  useEffect(() => {
    setDesignReady(false);
  }, [tfdesignUrl]);

  // Listen for tf-design iframe ready signal
  useEffect(() => {
    const handleMessage = (event) => {
      const { data } = event;
      if (!data || typeof data !== "object") return;
      if (data.type === "tfdesign-ready") {
        setDesignReady(true);
        setDesignError(null);
      }
      if (data.type === "tfdesign-error") {
        console.error("[MainView] Design error:", data.error);
        setDesignError(data.error || "Unknown error");
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Fetch ToothFairyAI models suitable for design (interleaved + supportsVision).
  useEffect(() => {
    if (!isElectronRunning()) return;
    const fetchDesignModels = async () => {
      try {
        const response = await fetch(
          `${envConfig.REACT_APP_AI_REST_ENDPOINT}/models_list`,
        );
        if (!response.ok) return;
        const data = await response.json();
        const templates = data?.templates || {};
        const filtered = Object.entries(templates)
          .filter(
            ([, m]) =>
              m?.deploymentType === "serverless" &&
              m?.interleaved === true &&
              m?.supportsVision === true &&
              !m?.deprecated,
          )
          .map(([id, m]) => {
            // The tf-design daemon uses "toothfairyai/<id>" format for TF models,
            // but the REST API returns bare IDs like "kimi-k2p6". Prefix with
            // "toothfairyai/" unless the ID already contains a "/".
            const daemonId = id.includes("/") ? id : `toothfairyai/${id}`;
            return { id: daemonId, name: m.name || id };
          });
        setDesignModels(filtered);
      } catch (e) {
        console.warn("[MainView] Failed to fetch design models:", e);
      }
    };
    fetchDesignModels();
    // Refresh models every 10 minutes
    const interval = setInterval(fetchDesignModels, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Sync app theme with tf-design iframe whenever theme changes.
  useEffect(() => {
    const iframe = designIframeRef.current;
    if (!iframe?.contentWindow || !tfdesignUrl) return;
    const theme = mode === "dark" ? "dark" : "light";
    const accent = currentTheme?.theme?.primary || undefined;
    iframe.contentWindow.postMessage(
      { type: "tfdesign-theme", theme, accent },
      "*",
    );
  }, [mode, currentTheme, tfdesignUrl]);

  // Send locale changes to tf-design iframe.
  useEffect(() => {
    const iframe = designIframeRef.current;
    if (!iframe?.contentWindow || !tfdesignUrl || !designReady) return;
    const lang = i18n?.language || "en";
    console.log("[MainView] sending tfdesign-locale:", lang);
    iframe.contentWindow.postMessage(
      { type: "tfdesign-locale", locale: lang },
      "*",
    );
  }, [tfdesignUrl, designReady, i18n?.language]);

  // Send available models to tf-design iframe when ready or models change.
  useEffect(() => {
    const iframe = designIframeRef.current;
    if (
      !iframe?.contentWindow ||
      !tfdesignUrl ||
      !designReady ||
      designModels.length === 0
    )
      return;
    iframe.contentWindow.postMessage(
      { type: "tfdesign-models", models: designModels },
      "*",
    );
  }, [tfdesignUrl, designReady, designModels]);

  // Tell tf-design which host OS it's running on so it can apply
  // platform-specific chrome (e.g. the macOS menu-item drop). Re-sent whenever
  // `platform` resolves late so the design app never misses the macOS signal.
  useEffect(() => {
    const iframe = designIframeRef.current;
    if (!iframe?.contentWindow || !tfdesignUrl || !designReady) return;
    const os =
      platform === "darwin"
        ? "macos"
        : platform === "win32"
          ? "windows"
          : platform === "linux"
            ? "linux"
            : null;
    if (!os) return;
    iframe.contentWindow.postMessage({ type: "tfdesign-platform", os }, "*");
  }, [tfdesignUrl, designReady, platform]);
  // Log active tab changes
  useEffect(() => {
    if (window?.electronAPI?.writeLog) {
      window.electronAPI.writeLog("info", "Tab changed", {
        activeTab,
        tfcodeUrlPresent: !!tfcodeUrl,
      });
    }
  }, [activeTab]);

  // Inject App theme into tfcode while preserving our custom icon/border tweaks.
  const buildTfcodeThemeCss = useCallback(() => {
    const t = currentTheme?.theme;
    if (!t) return "";
    const isDark = mode === "dark";
    const bg = t.backgroundColor || (isDark ? "#0c0c0c" : "#ffffff");
    const border2 = t.border_color || (isDark ? "#232323" : "#E8E8E8");
    const outline = t.app_outline_color || border2;
    const primary = t.primary || "#1db8c6";
    const orange = t.orange || "#f97316";
    const red = t.red || "#ef4444";

    const bodyBg = `html[data-theme], html[data-theme] body { background-color: ${bg} !important; }`;
    const surfaceBase = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
    const surfaceInset = isDark ? "#1a1a1a" : "#EFEFEF";
    const surfaceRaised = isDark ? "#272727" : "#EFEFEF";
    const surfaceRaisedHover = isDark ? "#333" : "#e0e0e0";
    const surfaceStrongerNonAlpha = isDark ? "#1c1c1c" : "#EFEFEF";
    const surfaceFloat = isDark ? "#1a1a1a" : "#ffffff";
    const bgDark = isDark ? "#1a1a1a" : bg;
    const bgAll = `:root {
      --background-base: ${bgDark};
      --background-weak: ${bgDark};
      --background-strong: ${bgDark};
      --background-stronger: ${bgDark};
      --surface-base: ${surfaceBase};
      --surface-inset-base: ${surfaceInset};
      --surface-raised-base: ${surfaceRaised};
      --surface-raised-base-hover: ${surfaceRaisedHover};
      --surface-raised-base-active: ${surfaceRaisedHover};
      --surface-raised-strong: ${surfaceRaisedHover};
      --surface-raised-stronger: ${surfaceInset};
      --surface-raised-stronger-non-alpha: ${surfaceStrongerNonAlpha};
      --surface-float-base: ${surfaceFloat};
    }`;
    const tooltipFix = `[data-component="tooltip"] { background: ${
      isDark ? "#1a1a1a" : "#ffffff"
    } !important; color: ${
      isDark ? "#ffffff" : "#000000"
    } !important; border: 1px solid ${border2} !important; }`;
    const iconOrange = `[data-component="icon-button"][data-variant="ghost"]:active:not(:disabled):not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], [data-component="icon-button"][data-variant="ghost"][aria-pressed="true"]:not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], [data-component="icon-button"][data-variant="ghost"][aria-selected="true"]:not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], [data-component="icon-button"][data-variant="ghost"][data-state="active"]:not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], .group\\/terminal-toggle[aria-expanded="true"] [data-slot="icon-svg"], .group\\/review-toggle[aria-expanded="true"] [data-slot="icon-svg"], [aria-expanded="true"].titlebar-icon [data-slot="icon-svg"], [aria-current="page"].titlebar-icon [data-slot="icon-svg"], .text-icon-strong [data-slot="icon-svg"] { color: ${orange} !important; }`;
    const noBg = `[data-component="icon-button"][data-variant="ghost"]:hover:not(:disabled), [data-component="icon-button"][data-variant="ghost"]:focus-visible:not(:disabled), [data-component="icon-button"][data-variant="ghost"]:active:not(:disabled), [data-component="icon-button"][data-variant="ghost"][aria-pressed="true"], [data-component="icon-button"][data-variant="ghost"][aria-selected="true"], [data-component="icon-button"][data-variant="ghost"][data-state="active"], .group\\/terminal-toggle, .group\\/review-toggle, .titlebar-icon { background-color: transparent !important; }`;
    const promptInputBg = isDark ? "#272727" : "#EFEFEF";
    const promptInputFix = `[data-action="prompt-submit"] [data-component="icon"] { width: 18px !important; height: 18px !important; } [data-component="icon-button"][data-icon="send"] [data-slot="icon-svg"] { color: ${primary} !important; } [data-component="icon-button"][data-icon="stop"] [data-slot="icon-svg"], [data-component="icon-button"][data-icon="stopGeneration"] [data-slot="icon-svg"] { color: ${red} !important; } [data-component="prompt-input"] { background-color: ${promptInputBg} !important; }`;
    const searchBoxFix = ``;
    const menuBgFix = `[data-component="dialog"] [data-slot="dialog-content"], [data-component="popover-content"], [data-component="dropdown-menu-content"], [data-component="dropdown-menu-sub-content"] { background: ${
      isDark ? "var(--background-base)" : "#EFEFEF"
    } !important; }`;
    const fileTreeIconFix = `[data-component="filetree"] .group:hover [data-component="file-icon"] { color: ${primary} !important; }`;
    const textColor = t.font_color || (isDark ? "#ffffff" : "#000000");
    const borderFix = `html[data-theme] { --border-base: ${outline} !important; --border-weak-base: ${outline} !important; --border-weaker-base: ${outline} !important; }`;
    const textFix = `html[data-theme] { --text-base: ${textColor} !important; --text-strong: ${textColor} !important; --text-weak: ${textColor} !important; }`;
    const reviewIconFix = `html[data-theme] { --icon-interactive-base: ${primary} !important; --interactive: ${primary} !important; }`;
    const diffSelectionFix = `[data-line][data-selected-line] { background-color: ${primary}33 !important; box-shadow: inset 2px 0 0 ${primary} !important; } [data-column-number][data-selected-line] { background-color: ${primary}4d !important; color: #fff !important; }`;
    const inlineInputFix = `[data-component="inline-input"]:focus-within, [data-component="inline-input"] textarea:focus, [data-component="inline-input"] input:focus, [data-component="inline-input"] [contenteditable]:focus { box-shadow: none !important; outline: none !important; border-color: transparent !important; }`;
    const topbarBg = t.backgroundColorAppBorders || bg;
    const layoutFix = `[data-component="sidebar-nav-desktop"], [data-component="sidebar-nav-desktop"] > div, [data-component="sidebar-nav-mobile"], [data-component="sidebar-nav-mobile"] > div, nav[data-component="sidebar-nav-desktop"] .bg-background-base, nav[data-component="sidebar-nav-desktop"] .bg-background-stronger, nav[data-component="sidebar-nav-mobile"] .bg-background-base { background-color: ${topbarBg} !important; } header.h-10.shrink-0 { background-color: ${topbarBg} !important; }`;
    return `${bgAll} ${bodyBg} ${tooltipFix} ${iconOrange} ${noBg} ${promptInputFix} ${searchBoxFix} ${menuBgFix} ${fileTreeIconFix} ${borderFix} ${textFix} ${reviewIconFix} ${diffSelectionFix} ${inlineInputFix} ${layoutFix}`;
  }, [currentTheme, mode]);

  // Tenant web origin for the active region (per-region env). Passed to the
  // embedded tfcode/tfdesign apps so their "Accounts" settings links open the
  // dashboard for the region the installer selected.
  const appOriginParam = useMemo(() => {
    try {
      const url = new URL(envConfig?.REACT_APP_redirect_signin_uri || "");
      return url.protocol === "https:"
        ? `&appOrigin=${encodeURIComponent(url.origin)}`
        : "";
    } catch (_) {
      return "";
    }
  }, []);

  // Build the iframe src with theme params so oc-theme-preload.js can set correct background immediately
  const iframeSrc = useMemo(() => {
    if (!tfcodeUrl || !mode) return tfcodeUrl;
    const stripped = tfcodeUrl
      .replace(/[?&]theme=[^&]*/g, "")
      .replace(/[?&]lang=[^&]*/g, "")
      .replace(/[?&]bg=[^&]*/g, "")
      .replace(/[?&]workspace=[^&]*/g, "")
      .replace(/[?&]appOrigin=[^&]*/g, "")
      .replace(/[?&]$/, "");
    const sep = stripped.includes("?") ? "&" : "?";
    const lang = i18n?.language || "en";
    const bgParam =
      mode === "dark"
        ? `&bg=${encodeURIComponent(
            currentTheme?.theme?.backgroundColor || "#0c0c0c",
          )}`
        : "";
    // Pass the active workspace id to the tfcode app so account/dashboard
    // links can open the workspace-scoped settings page at runtime (the tfcode
    // web bundle is built once, so it can't bake the id at build time).
    const workspaceParam = state?.activeWorkspaceID
      ? `&workspace=${encodeURIComponent(state.activeWorkspaceID)}`
      : "";
    return `${stripped}${sep}theme=${mode}&lang=${lang}${bgParam}${workspaceParam}${appOriginParam}`;
  }, [
    tfcodeUrl,
    mode,
    i18n?.language,
    currentTheme?.theme?.backgroundColor,
    state?.activeWorkspaceID,
    appOriginParam,
  ]);

  const sendEmbedConfig = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(
      {
        type: "tfcode-inject-css",
        css: buildTfcodeThemeCss(),
        mode,
      },
      "*",
    );
  }, [mode, buildTfcodeThemeCss]);

  // Forward workspace logo + host OS to the embedded tfcode so its sidebar
  // matches the app sidebar (logo header + platform-consistent chrome).
  const sendEmbedBridge = useCallback(() => {
    const logo = latestWorkspaceLogo.current;
    const targets = [iframeRef.current, panelIframeRef.current];
    for (const iframe of targets) {
      if (!iframe || !iframe.contentWindow) continue;
      iframe.contentWindow.postMessage(
        {
          v: 1,
          type: "tfcode-workspace-logo",
          url: logo?.url ?? null,
          isWide: logo?.isWide ?? false,
        },
        "*",
      );
      iframe.contentWindow.postMessage(
        {
          v: 1,
          type: "tfcode-platform",
          os: embedPlatform.current,
        },
        "*",
      );
    }
  }, []);

  const toggleEmbeddedSidebar = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ type: "tfcode-sidebar-toggle" }, "*");
    }
  }, []);

  const toggleEmbeddedDesignSidebar = useCallback(() => {
    const iframe = designIframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "tfdesign-sidebar-toggle" },
        "*",
      );
    }
  }, []);

  useEffect(() => {
    const targets = [iframeRef.current, panelIframeRef.current];
    for (const iframe of targets) {
      if (!iframe?.contentWindow) continue;
      iframe.contentWindow.postMessage(
        {
          v: 1,
          type: "tfcode-workspace-logo",
          url: workspaceLogo?.url ?? null,
          isWide: workspaceLogo?.isWide ?? false,
        },
        "*",
      );
    }
  }, [workspaceLogo, tfcodeUrl]);

  // Build the tf-design iframe src with theme/lang params
  const tfdesignSrc = useMemo(() => {
    if (!tfdesignUrl || !mode) return tfdesignUrl;
    const stripped = tfdesignUrl
      .replace(/[?&]theme=[^&]*/g, "")
      .replace(/[?&]lang=[^&]*/g, "")
      .replace(/[?&]bg=[^&]*/g, "")
      .replace(/[?&]embed=[^&]*/g, "")
      .replace(/[?&]workspace=[^&]*/g, "")
      .replace(/[?&]appOrigin=[^&]*/g, "")
      .replace(/[?&]$/, "");
    const sep = stripped.includes("?") ? "&" : "?";
    const lang = i18n?.language || "en";
    const bgParam =
      mode === "dark"
        ? `&bg=${encodeURIComponent(
            currentTheme?.theme?.backgroundColor || "#0c0c0c",
          )}`
        : "";
    // Pass the active workspace id to the design app so account/dashboard
    // links can open the workspace-scoped settings page at runtime (the design
    // web bundle is built once, so it can't bake the id at build time).
    const workspaceParam = state?.activeWorkspaceID
      ? `&workspace=${encodeURIComponent(state.activeWorkspaceID)}`
      : "";
    const result = `${stripped}${sep}theme=${mode}&lang=${lang}${bgParam}&embed=electron${workspaceParam}${appOriginParam}`;
    console.log(
      "[MainView] tfdesignSrc:",
      result,
      "from tfdesignUrl:",
      tfdesignUrl,
    );
    return result;
  }, [
    tfdesignUrl,
    mode,
    i18n?.language,
    currentTheme?.theme?.backgroundColor,
    state?.activeWorkspaceID,
    appOriginParam,
  ]);

  const sendTfdesignEmbedConfig = useCallback(() => {
    const iframe = designIframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    // Send theme config that tf_design's postMessage bridge expects
    iframe.contentWindow.postMessage(
      {
        type: "tfdesign-theme",
        theme: mode,
        accent:
          currentTheme?.theme?.accentColor || currentTheme?.theme?.primaryColor,
      },
      "*",
    );
  }, [
    mode,
    currentTheme?.theme?.accentColor,
    currentTheme?.theme?.primaryColor,
  ]);

  useEffect(() => {
    if (!isCodeTabActive) return;
    sendEmbedConfig();
  }, [isCodeTabActive, mode, sendEmbedConfig]);

  useEffect(() => {
    if (!isDesignTabActive) return;
    sendTfdesignEmbedConfig();
  }, [isDesignTabActive, mode, sendTfdesignEmbedConfig]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const handleLoad = () => {
      if (window?.electronAPI?.writeLog) {
        window.electronAPI.writeLog("info", "Code iframe loaded");
      }
      sendEmbedConfig();
      sendEmbedBridge();
    };
    iframe.addEventListener("load", handleLoad);
    return () => iframe.removeEventListener("load", handleLoad);
  }, [tfcodeUrl, sendEmbedConfig, sendEmbedBridge]);

  useEffect(() => {
    const iframe = designIframeRef.current;
    if (!iframe) return;
    const handleLoad = () => {
      if (window?.electronAPI?.writeLog) {
        window.electronAPI.writeLog("info", "Design iframe loaded");
      }
      sendTfdesignEmbedConfig();
    };
    iframe.addEventListener("load", handleLoad);
    return () => iframe.removeEventListener("load", handleLoad);
  }, [tfdesignUrl, sendTfdesignEmbedConfig]);

  // Listen for postMessages from the tfcode and tfdesign iframes
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === "tfcode-ready") {
        if (window?.electronAPI?.writeLog) {
          window.electronAPI.writeLog("info", "Code app ready");
        }
        setCodeReady(true);
        sendEmbedBridge();
        // Send stored bound folders now that iframe is fully loaded and listening
        const folderTargets = [iframeRef.current, panelIframeRef.current];
        for (const iframe of folderTargets) {
          if (iframe?.contentWindow && latestBoundFolders.current) {
            iframe.contentWindow.postMessage(
              {
                v: 1,
                type: "tfcode-bound-folders",
                folders: latestBoundFolders.current,
              },
              "*",
            );
          }
        }
      }
      if (event.data?.type === "tfdesign-ready") {
        if (window?.electronAPI?.writeLog) {
          window.electronAPI.writeLog("info", "Design app ready");
        }
        setDesignReady(true);
      }
      if (event.data?.type === "tfcode-unbind-folder") {
        const path = event.data?.path;
        if (!path) {
          return;
        }
        const chat = (state?.chats || []).find((c) => {
          const meta =
            typeof c?.metadata === "string"
              ? JSON.parse(c.metadata)
              : c?.metadata;
          return meta?.folderBinding?.path === path;
        });
        if (!chat?.id) {
          return;
        }
        // Optimistically update local state for instant UI refresh
        const updatedChats = (state?.chats || []).map((c) => {
          if (c.id !== chat.id) return c;
          const meta =
            typeof c.metadata === "string"
              ? JSON.parse(c.metadata)
              : c.metadata;
          const { folderBinding, ...rest } = meta || {};
          return { ...c, metadata: JSON.stringify(rest) };
        });
        dispatch("SET_CHATS", updatedChats);
        // Use dynamic import to avoid circular dependency with chatsHelper
        import("../API/Chats/chatsHelper")
          .then((m) => {
            const helper = m.default;
            return helper.getChat(chat.id).then((chatData) => {
              const meta = chatData?.data?.getChat?.metadata
                ? JSON.parse(chatData.data.getChat.metadata)
                : {};
              const { folderBinding, ...updatedMeta } = meta;
              return helper.updateChat(chat.id, [
                { property: "metadata", value: JSON.stringify(updatedMeta) },
              ]);
            });
          })
          .then((result) => {})
          .catch((err) => {
            window?.electronAPI?.writeLog?.("error", "folder unbind failed", {
              chatId: chat?.id,
              error: err?.message,
            });
          });
      }
      if (event.data?.type === "tfcode-list-dir") {
        const path = event.data?.path;
        if (!path) {
          return;
        }
        if (!window.electronAPI?.listFiles) {
          return;
        }
        window.electronAPI
          .listFiles(path)
          .then((result) => {
            const iframe = iframeRef.current;
            if (iframe?.contentWindow) {
              iframe.contentWindow.postMessage(
                {
                  type: "tfcode-dir-listing",
                  path,
                  entries: result?.files ?? [],
                },
                "*",
              );
            }
          })
          .catch((err) => {
            window?.electronAPI?.writeLog?.("error", "list-dir failed", {
              path,
              error: err?.message,
            });
          });
      }
      if (event.data?.type === "tfcode-read-file") {
        const path = event.data?.path;
        const id = event.data?.id;
        if (!path || !id) {
          return;
        }
        if (!window.electronAPI?.readFile) {
          return;
        }
        window.electronAPI
          .readFile(path)
          .then((result) => {
            const iframe = iframeRef.current;
            if (!iframe?.contentWindow) return;
            if (result?.success) {
              iframe.contentWindow.postMessage(
                {
                  type: "tfcode-file-content",
                  id,
                  content: result.content,
                  encoding: result.encoding,
                },
                "*",
              );
            } else {
              iframe.contentWindow.postMessage(
                {
                  type: "tfcode-file-error",
                  id,
                  error: result?.error || "Failed to read file",
                },
                "*",
              );
            }
          })
          .catch((err) => {
            const iframe = iframeRef.current;
            if (!iframe?.contentWindow) return;
            iframe.contentWindow.postMessage(
              { type: "tfcode-file-error", id, error: err.message },
              "*",
            );
          });
      }
      // --- New panel messages (tfcode overlay) ---
      // Validate origin against the known panel iframe origin when available.
      const panelOrigin = (() => {
        const url = tfcodePanelUrlRef.current;
        if (!url) return null;
        try {
          return new URL(url).origin;
        } catch {
          return null;
        }
      })();
      const isPanelMessage =
        event.data?.type === "tfcode-sessions" ||
        event.data?.type === "tfcode-screenshot-request" ||
        event.data?.type === "tfcode-navigate";
      if (isPanelMessage) {
        if (!event.origin || event.origin === "null") {
          console.warn(
            "[tfcode] rejected panel message with empty origin",
            event.data?.type,
          );
          return;
        }
        if (panelOrigin && event.origin !== panelOrigin) {
          console.warn(
            "[tfcode] rejected panel message from unexpected origin",
            event.origin,
            "expected",
            panelOrigin,
          );
          return;
        }
        const v = event.data?.v ?? 0;
        if (v < 1) {
          console.warn(
            "[tfcode] rejected panel message with invalid version",
            v,
            event.data?.type,
          );
          return;
        }
      }
      if (event.data?.type === "tfcode-sessions") {
        const sessions = event.data?.sessions;
        if (!Array.isArray(sessions)) return;
        if (window?.electronAPI?.writeLog) {
          window.electronAPI.writeLog("info", "tfcode sessions received", {
            count: sessions.length,
          });
        }
        dispatch("SET_TFCODE_SESSIONS", sessions);
        window.__tfcodeSessions = sessions;
      }
      if (event.data?.type === "tfcode-screenshot-request") {
        const id = event.data?.id;
        console.log("[tfcode-screenshot] host received request", { id });
        if (!id) return;
        captureHostScreenshot(id, tfcodePanelUrlRef.current).catch((err) => {
          console.error("[tfcode-screenshot] host capture threw", err);
          if (window?.electronAPI?.writeLog) {
            window.electronAPI.writeLog("error", "screenshot capture failed", {
              id,
              error: err?.message,
            });
          }
        });
      }
      if (event.data?.type === "tfcode-navigate") {
        const target = event.data?.target;
        const params = event.data?.params || {};
        // The host's active workspace is the source of truth for scope: the
        // embedded tfcode session can be stale right after a workspace switch
        // (the sidecar restarts asynchronously), so tfcode-supplied ids must
        // not drag the host back to the previous (e.g. starred) workspace.
        // Fall back to tfcode's params only when the host has none active.
        const workspaceId = state?.activeWorkspaceID || params?.workspaceId;
        // Workspace-scoped sections (template contains :workspaceId) require an
        // active workspace, so skip them when none is active (would 404/blank).
        // Workspaceless sections — `profile` (/profile/:sectionId?) and
        // `workspaces` (/workspaces) — must still be reachable from the
        // workspaces list where no workspace is active.
        const template =
          typeof routes?.[target] === "string" ? routes[target] : null;
        const needsWorkspace = template
          ? template.includes(":workspaceId")
          : false;
        if (target && template && (!needsWorkspace || workspaceId)) {
          const path = routeFromSignal(target, params, workspaceId);
          if (path && history.location?.pathname !== path) {
            history.push(path);
          }
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [activeTab, setActiveTab, sendEmbedBridge, state?.activeWorkspaceID]);

  // Recover a blank Code screen. If the tab is active with a URL but the
  // embed never signals tfcode-ready (iframe load event lost / sidecar boot
  // race on a reused port — seen in the wild after a workspace switch):
  //   1. after 10s, remount the iframe (same src, new React key);
  //   2. after a further 10s, restart the sidecar via IPC and rebuild the URL.
  // The whole effect re-runs whenever codeReloadKey bumps, walking the
  // attempts forward; codeReady cancels it. At most one sidecar restart per
  // blank episode — never loops.
  const codeReloadAttemptsRef = useRef(0);
  useEffect(() => {
    codeReloadAttemptsRef.current = 0;
  }, [tfcodeUrl]);
  useEffect(() => {
    if (!isCodeTabActive || !tfcodeUrl || codeReady) return undefined;
    const log = (level, message, data) => {
      if (window?.electronAPI?.writeLog) {
        window.electronAPI.writeLog(level, message, data);
      }
    };
    const timer = setTimeout(async () => {
      const attempt = codeReloadAttemptsRef.current;
      if (attempt === 0) {
        // First strike: remount the iframe with the same src.
        codeReloadAttemptsRef.current = 1;
        console.error("[MainView] Code screen appears blank after timeout");
        log("error", "Code screen blank after load timeout — reloading iframe", {
          src: iframeSrc,
        });
        setCodeReloadKey((k) => k + 1);
        return;
      }
      if (attempt === 1) {
        // Second strike: restart the sidecar (once only).
        codeReloadAttemptsRef.current = 2;
        console.error(
          "[MainView] Code screen blank — restarting sidecar",
          iframeSrc,
        );
        log("error", "Code screen blank after reload — restarting sidecar", {
          src: iframeSrc,
        });
        try {
          await window.electronAPI.stopTfcode();
        } catch (e) {
          console.warn("[MainView] stopTfcode before restart failed:", e);
        }
        try {
          const { port } = await window.electronAPI.startTfcode();
          setTfcodeUrl(`http://localhost:${port}`);
          setCodeReloadKey((k) => k + 1);
        } catch (e) {
          console.error("[MainView] Code sidecar restart failed:", e);
          log("error", "Code sidecar restart failed", { error: e?.message });
        }
        return;
      }
      log("error", "Code screen still blank after sidecar restart", {
        src: iframeSrc,
      });
    }, 10000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCodeTabActive, tfcodeUrl, iframeSrc, codeReady, codeReloadKey]);

  // Fetch announcement from backend
  const fetchAnnouncement = useCallback(async () => {
    try {
      const baseUrl = envConfig.REACT_APP_REST_ENDPOINT?.replace(/\/$/, "");
      const response = await fetch(
        `${baseUrl}/global_utils/fetch_announcement`,
      );
      if (response.ok) {
        const data = await response.json();
        setAnnouncementData(data);
      }
    } catch (error) {
      console.error("Failed to fetch announcement:", error);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncement();
  }, [fetchAnnouncement]);

  const { params } = usePath();
  const { setCurrentView } = useContext(AppRootContext);
  const { signOut } = AppAuth.useAuth();
  const AppToasty = AppToast.useToast();
  const { isWorkspaceEnabled, getActiveWorkspace } = useWorkspaces();

  // Bridge workspace state to Electron for tfcode
  useElectronWorkspace();

  // The embedded tfcode asked the host to sign in: hand the active
  // workspace's credentials to the sidecar. The app's Cognito session is
  // normally already established; the workspaceToken plays the API-key role
  // for the sidecar's ToothFairyAI calls (models_list, workspace agents).
  const handleTfcodeSignIn = useCallback(() => {
    const iframe = panelIframeRef.current;
    if (!iframe?.contentWindow) return;
    const workspace = getActiveWorkspace();
    if (!workspace?.workspaceToken || !workspace?.id) {
      AppToasty.show(
        t("tfcodePanel.signInFirst", {
          defaultValue: "Sign in to ToothFairyAI first.",
        }),
        { type: "info" },
      );
      return;
    }
    const targetOrigin = (() => {
      const src = iframe.src;
      if (!src) return "*";
      try {
        return new URL(src).origin;
      } catch {
        return "*";
      }
    })();
    // App env -> sidecar region (see packages/tfcode REGION_URLS).
    const regionMap = {
      dev: "dev",
      prod: "au",
      prodeu: "eu",
      produs: "us",
    };
    iframe.contentWindow.postMessage(
      {
        v: 1,
        type: "tfcode-credentials",
        apiKey: workspace.workspaceToken,
        workspaceId: workspace.id,
        region: regionMap[envConfig.REACT_APP_ENV] || "dev",
      },
      targetOrigin,
    );
  }, [getActiveWorkspace, AppToasty, t]);

  // Fetch models on load and whenever the active workspace changes, and store
  // in global state. The workspace (id + API key) is passed so the backend
  // appends this workspace's fine-tuned models (registry ToothFairyAI/… ids
  // with tier pricing) — without it custom models never appear and can't be
  // served/tracked properly.
  const activeWorkspaceToken = getActiveWorkspace()?.workspaceToken;
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const activeWorkspace = getActiveWorkspace();
        const workspace = activeWorkspace?.id
          ? {
              id: activeWorkspace.id,
              apiKey: activeWorkspace?.workspaceToken,
            }
          : null;
        const result = await modelsHelper.getServerlessModelsForUI(
          false,
          workspace,
        );
        const regionalResult = await modelsHelper.getServerlessModelsForUI(
          true,
          workspace,
        );
        const trainableModels = await modelsHelper.getTrainableModelsForUI();
        const regionalIds = regionalResult.baseModels.map((m) => m.id);

        dispatch("SET_MODELS", {
          functionCallingModels: result.functionCallingModels,
          baseModels: result.baseModels,
          visionModels: result.visionModels,
          trainableModels: trainableModels,
          regionalModelIds: regionalIds,
        });
      } catch (error) {
        console.error("[MainViewScreen] Failed to fetch models:", error);
      }
    };

    const fetchExternalModels = async () => {
      try {
        const result = await modelsHelper.getExternalModelsForUI();
        dispatch("SET_EXTERNAL_MODELS", {
          models: result.models,
          deprecatedModels: result.deprecatedModels,
          providers: result.providers,
        });
      } catch (error) {
        console.error(
          "[MainViewScreen] Failed to fetch external models:",
          error,
        );
      }
    };

    fetchModels();
    fetchExternalModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.activeWorkspaceID, activeWorkspaceToken]);
  const { user, updateUser: updateLocalUser } = AppUser.useUser();
  const {
    showReleaseNotes,
    releaseNotes,
    currentVersion,
    markReleaseNotesAsViewed,
    setShowReleaseNotes,
  } = useReleaseNotes();
  const {
    showNewsletterConsent,
    setShowNewsletterConsent,
    isUpdating: isNewsletterConsentUpdating,
    handleAccept: handleNewsletterAccept,
    handleDecline: handleNewsletterDecline,
  } = useNewsletterConsent();
  useEffect(() => {
    if (
      user &&
      getActiveWorkspace()?.id &&
      getActiveWorkspace()?.isMFAEnabled &&
      !user?.isMFAEnabled
    ) {
      dispatch("SET_PAGE_NOT_FOUND", true);
      history.push("/mfa_required");
    }
  }, [user?.isMFAEnabled, getActiveWorkspace()?.isMFAEnabled]);
  const handleSignOut = async () => {
    if (window.electronAPI) {
      window.electronAPI
        .logout()
        .then(async () => {
          AppToasty.show(t("legacy.LogoutMessage"), {
            placement: "top",
            type: "success",
          });
          history.push("/");
          setCurrentView("auth");
          if (window?.electronAPI && window?.electronAPI?.clearTokens) {
            window.electronAPI.clearTokens();
          }
        })
        .catch((error) => {
          console.error("Auth - logout failed", error);
          AppToasty.show(t("LogoutFailure"), {
            placement: "top",
            type: "danger",
          });
        });
    } else
      signOut()
        .then(async () => {
          AppToasty.show(t("legacy.LogoutMessage"), {
            placement: "top",
            type: "success",
          });
          history.push("/");
          setCurrentView("auth");
          if (window?.electronAPI && window?.electronAPI?.clearTokens) {
            window.electronAPI.clearTokens();
          }
        })
        .catch((error) => {
          console.error("Auth - logout failed", error);
          AppToasty.show(t("LogoutFailure"), {
            placement: "top",
            type: "danger",
          });
        });
  };
  const sideMenu = useMemo(() => {
    return (
      <SideMenu handleSignOut={handleSignOut} isInWorkspace={isInWorkspace} />
    );
  }, [isInWorkspace, handleSignOut, userType()]);

  useEffect(() => {
    let removeListener = () => {};

    if (typeof window?.electronAPI?.onNavigate === "function") {
      // Check if the API is available
      removeListener = window.electronAPI.onNavigate((path) => {
        console.log("App Navigate (from Electron):", path); // THIS IS THE LOG YOU WANT
        history.push(path);
      });
    } else {
      console.warn(
        "window.electronAPI.onNavigate is not available. Ensure preload script is loaded and exposing the API.",
      );
    }

    return () => {
      removeListener(); // Call the cleanup function returned by onNavigate
    };
  }, [history]);

  useEffect(() => {
    if (params?.workspaceId) {
      dispatch("SET_ACTIVE_WORKSPACE_ID", params?.workspaceId);
    }
  }, [params?.workspaceId]);

  // Check if running in Electron (real runtime or scoped browser override)
  const isElectron = isElectronChrome();

  useEffect(() => {
    const shouldShowAnnouncement =
      !isElectron && // Hide banner in Electron app
      announcementData?.announcement &&
      announcementData?.lastAnnouncementInt &&
      user?.lastAnnouncementViewedInt !== announcementData?.lastAnnouncementInt;
    setShowAnnouncement(shouldShowAnnouncement);
  }, [
    envConfig,
    isElectron,
    announcementData?.announcement,
    announcementData?.lastAnnouncementInt,
    user?.lastAnnouncementViewedInt,
  ]);

  const handleAnnouncementClose = async () => {
    setShowAnnouncement(false);
    if (announcementData?.lastAnnouncementInt && user?.id) {
      try {
        await usersAPI.updateUser({
          id: user?.id,
          lastAnnouncementViewedInt: announcementData?.lastAnnouncementInt,
        });
        updateLocalUser({
          ...user,
          lastAnnouncementViewedInt: announcementData?.lastAnnouncementInt,
        });
      } catch (error) {
        console.error("Failed to update lastAnnouncementViewedInt:", error);
      }
    }
  };

  const isCodeTabActive = activeTab === "code" && tfcodeUrl;
  const isDesignTabActive = activeTab === "design" && tfdesignUrl;
  const isCodeOrDesignTab = activeTab === "code" || activeTab === "design";
  const isFullViewTab = isCodeTabActive || isDesignTabActive;
  // When App is not in the user's selection and a workspace is active, we are
  // about to auto-switch to Code/Design (see the auto-switch useLayoutEffect).
  // Hide App chrome (SideMenu back button) during this frame so it never
  // paints before the switch — mirrors the children suppression below.
  const shouldHideAppChrome =
    activeTab === "app" && !installedApps.includes("app") && isInWorkspace;

  return (
    <>
      {!shouldHideAppChrome && (
        <View style={isCodeOrDesignTab ? _styles.hiddenSideMenu : undefined}>
          {sideMenu}
        </View>
      )}

      <View
        dataSet={{ "app-content": "" }}
        style={[
          isFullViewTab ? _styles.codeContent : _styles.content,
          isFullViewTab && {
            backgroundColor:
              currentTheme?.theme?.backgroundColor ||
              (mode === "dark" ? "#0c0c0c" : "#ffffff"),
          },
        ]}
      >
        {showAnnouncement && !isFullViewTab && (
          <AnnouncementBanner
            announcement={announcementData?.announcement || ""}
            onClose={handleAnnouncementClose}
          />
        )}
        {!showAnnouncement && !isElectron && !isFullViewTab && (
          <AppInstallBanner />
        )}
        {!state?.pageNotFound && (
          <TopBar
            handleSignOut={handleSignOut}
            isInWorkspace={isInWorkspace}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setTfcodeUrl={setTfcodeUrl}
            tfcodeUrl={tfcodeUrl}
            setTfcodePanelUrl={setTfcodePanelUrl}
            setTfdesignUrl={setTfdesignUrl}
            tfdesignUrl={tfdesignUrl}
            onToggleEmbeddedSidebar={toggleEmbeddedSidebar}
            onToggleEmbeddedDesignSidebar={toggleEmbeddedDesignSidebar}
          />
        )}
        {/* Spacer for fixed TopBar on Electron */}
        {chrome && !state?.pageNotFound && (
          <View style={_styles.topBarSpacer} />
        )}

        {/* Loading overlay until tfcode signals ready — after a workspace
            switch the sidecar restarts and re-syncs, so say that instead */}
        {activeTab === "code" && !codeReady && (
          <ToothFairyLoadingPlaceholder
            caption={
              switchedWorkspace ? "code.syncingProfile" : "code.loadingCode"
            }
            brandedAnimation
            style={{ borderRadius: 8 }}
          />
        )}

        {/* Loading overlay until tf-design signals ready */}
        {activeTab === "design" &&
          !designReady &&
          (designError ? (
            <View
              style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
                padding: 24,
              }}
            >
              <View style={{ alignItems: "center", maxWidth: 480 }}>
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "600",
                    marginBottom: 8,
                    textAlign: "center",
                  }}
                >
                  {t("design.startFailed")}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    opacity: 0.7,
                    textAlign: "center",
                    marginBottom: 16,
                  }}
                >
                  {designError}
                </Text>
                <Text
                  style={{ fontSize: 12, opacity: 0.5, textAlign: "center" }}
                >
                  Check logs at: ~/Library/Logs/ToothFairyAI/ (macOS) or
                  %APPDATA%\Roaming\ToothFairyAI\logs\ (Windows)
                </Text>
              </View>
            </View>
          ) : (
            <ToothFairyLoadingPlaceholder
              caption="design.loadingDesign"
              brandedAnimation
              style={{ borderRadius: 8 }}
            />
          ))}

        {/* Keep tfcode iframe mounted when loaded so switching tabs doesn't reload it */}
        {tfcodeUrl && (
          <View
            style={
              isCodeTabActive
                ? { flex: 1, position: "relative" }
                : {
                    position: "fixed",
                    left: -9999,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: -9999,
                    opacity: 1,
                  }
            }
          >
            <iframe
              ref={iframeRef}
              key={`code-iframe-${codeReloadKey}`}
              src={iframeSrc}
              allow="clipboard-write; microphone; camera"
              onError={(e) => {
                console.error("[MainView] Code iframe error:", e);
                if (window?.electronAPI?.writeLog) {
                  window.electronAPI.writeLog(
                    "error",
                    "Code iframe load error",
                    {
                      src: iframeSrc,
                      error: e?.message || "unknown",
                    },
                  );
                }
              }}
              style={{
                flex: 1,
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: 8,
                overflow: "hidden",
                backgroundColor:
                  currentTheme?.theme?.backgroundColor ||
                  (mode === "dark" ? "#0c0c0c" : "#ffffff"),
              }}
              allowTransparency={false}
              title="tfcode"
            />
          </View>
        )}

        {/* Keep tf-design iframe mounted when loaded so switching tabs doesn't reload it */}
        {tfdesignUrl && (
          <View
            style={
              isDesignTabActive
                ? { flex: 1, position: "relative" }
                : {
                    position: "fixed",
                    left: -9999,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: -9999,
                    opacity: 1,
                  }
            }
          >
            <iframe
              ref={designIframeRef}
              src={tfdesignSrc}
              allow="clipboard-write"
              onError={(e) => {
                console.error("[MainView] Design iframe error:", e);
                if (window?.electronAPI?.writeLog) {
                  window.electronAPI.writeLog(
                    "error",
                    "Design iframe load error",
                    {
                      src: tfdesignSrc,
                      error: e?.message || "unknown",
                    },
                  );
                }
              }}
              style={{
                flex: 1,
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: 8,
                overflow: "hidden",
                backgroundColor:
                  currentTheme?.theme?.backgroundColor ||
                  (mode === "dark" ? "#0c0c0c" : "#ffffff"),
              }}
              allowTransparency={false}
              title="tfdesign"
            />
          </View>
        )}
        {/* App content: suppressed when App is not selected and a workspace
            is already active (about to auto-switch to Code/Design). This
            prevents a one-frame flicker of the App workspace dashboard. */}
        {!isFullViewTab &&
          (shouldHideAppChrome ? (
            <View
              style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ToothFairyLoadingPlaceholder
                caption="common.loading"
                brandedAnimation
                style={{ borderRadius: 8 }}
              />
            </View>
          ) : (
            children
          ))}
      </View>

      <ReleaseNotesModal
        setVisible={setShowReleaseNotes}
        visible={showReleaseNotes}
        onClose={markReleaseNotesAsViewed}
        releaseNotes={releaseNotes}
        currentVersion={currentVersion}
      />
      <NewsletterConsentModal
        visible={showNewsletterConsent}
        setVisible={setShowNewsletterConsent}
        onAccept={handleNewsletterAccept}
        onDecline={handleNewsletterDecline}
        isUpdating={isNewsletterConsentUpdating}
      />

      <TfcodeSidePanel
        url={tfcodePanelUrl}
        iframeRef={panelIframeRef}
        onClose={() => setTfcodePanelUrl(null)}
        onSignIn={handleTfcodeSignIn}
      />
    </>
  );
};

const styles = ({
  height,
  sideMenuOpen,
  width,
  pageNotFound,
  isLargeScreen,
  electron,
  platform,
}) =>
  StyleSheet.create({
    container: {
      height: "100%",
      flexDirection: "row",
      width: "100%",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      overflow: "hidden",
    },
    hiddenSideMenu: {
      display: "none",
    },
    content: {
      // In Electron chrome the TopBar is a fixed full-width bar that must sit
      // above the sidebar; zIndex -1 would trap it underneath the sidebar.
      zIndex: electron ? 0 : -1,
      height: height,
      maxHeight: !pageNotFound && height,
      width: "100%",
      maxWidth:
        !pageNotFound &&
        width - (sideMenuOpen && isLargeScreen && Constants.sideMenuWidth),
      transitionProperty: "max-width",
      transitionDuration: "0.25s",
      transitionTimingFunction: "ease-in-out",
    },
    codeContent: {
      zIndex: -1,
      height: height,
      maxHeight: height,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: 8,
      overflow: "hidden",
    },
    // Spacer for fixed TopBar on Electron
    topBarSpacer: {
      height: 35,
    },
  });

export default MainViewScreen;
